// Rozvrh, sezónní místa setkání a pauzy lokality (16.9.2026).
//
// Tři editory, které se používají na dvou místech: ve formuláři nové lokality
// (Přehled) i v nastavení existující (Detail). Proto bydlí tady a ne v jedné
// z těch stránek. Vzhled je schválně stejný jako editor tarifů: řádky polí
// field, mazání křížkem vpravo, přidání textovým odkazem pod tabulkou.

import React, { useState } from "react";
import { Plus, X, MapPin, CalendarOff, Clock, ChevronDown } from "lucide-react";

// Blok nastavení lokality (16.9.2026, odsouhlasený mockup). Nastavení bylo
// jedna dlouhá plocha, ve které vedle sebe stála adresa, uzávěrka přihlašování
// a ceny. Teď jsou čtyři bloky podle toho, kdy do nich člověk sáhne. Sbalený
// blok ukáže pod nadpisem souhrn, takže se nemusí rozbalovat kvůli kontrole.
export function Blok({ ikona: Ikona, nazev, souhrn, popis, vychoziOtevreno = false, children }) {
  const [otevreno, setOtevreno] = useState(vychoziOtevreno);
  return (
    <div className={`overflow-hidden rounded-2xl border transition ${otevreno ? "border-brand-200 bg-white" : "border-slate-200 bg-slate-50/60"}`}>
      <button
        type="button"
        onClick={() => setOtevreno((o) => !o)}
        aria-expanded={otevreno}
        className="flex w-full items-center gap-3 px-3 py-3 text-left sm:px-4"
      >
        <span className={`grid h-9 w-9 shrink-0 place-items-center rounded-xl border ${otevreno ? "border-brand-200 bg-brand-50 text-brand-deep" : "border-slate-200 bg-white text-slate-400"}`}>
          {Ikona && <Ikona className="h-4 w-4" />}
        </span>
        <span className="min-w-0 flex-1">
          <span className="block text-[15px] font-bold text-slate-900">{nazev}</span>
          <span className="block truncate text-xs text-slate-500">{otevreno ? popis : (souhrn || popis)}</span>
        </span>
        <ChevronDown className={`h-5 w-5 shrink-0 text-slate-400 transition-transform ${otevreno ? "" : "-rotate-90"}`} />
      </button>
      {otevreno && <div className="space-y-4 border-t border-slate-100 px-3 py-4 sm:px-4">{children}</div>}
    </div>
  );
}

export const DNY = [
  { id: 1, zkratka: "Po", nazev: "pondělí" },
  { id: 2, zkratka: "Út", nazev: "úterý" },
  { id: 3, zkratka: "St", nazev: "středa" },
  { id: 4, zkratka: "Čt", nazev: "čtvrtek" },
  { id: 5, zkratka: "Pá", nazev: "pátek" },
  { id: 6, zkratka: "So", nazev: "sobota" },
  { id: 7, zkratka: "Ne", nazev: "neděle" },
];

const MESICE = [
  "ledna", "února", "března", "dubna", "května", "června",
  "července", "srpna", "září", "října", "listopadu", "prosince",
];
// Poslední den měsíce. U února schválně 29, porovnává se jen MM-DD a v roce
// bez 29. února se stejně žádné datum nad 02-28 nevyskytne.
const POSLEDNI = ["31", "29", "31", "30", "31", "30", "31", "31", "30", "31", "30", "31"];

function Nadpis({ ikona: Ikona, children, popis }) {
  return (
    <div className="mb-1.5">
      <div className="flex items-center gap-1.5 text-sm font-medium text-slate-700">
        {Ikona && <Ikona className="h-4 w-4 text-slate-400" />}
        {children}
      </div>
      {popis && <div className="text-xs text-slate-500 mt-0.5">{popis}</div>}
    </div>
  );
}

function Smazat({ onClick, popis }) {
  return (
    <button type="button" onClick={onClick} title={popis} aria-label={popis}
      className="inline-flex h-[46px] w-[46px] shrink-0 items-center justify-center rounded-xl border border-slate-200 text-slate-400 transition hover:border-rose-200 hover:bg-rose-50 hover:text-rose-600">
      <X className="h-4 w-4" />
    </button>
  );
}

function PridatOdkaz({ onClick, children }) {
  return (
    <button type="button" onClick={onClick}
      className="inline-flex items-center gap-1 text-sm font-medium text-brand-700 hover:underline">
      <Plus className="h-3.5 w-3.5" /> {children}
    </button>
  );
}

/** Výběr měsíce pro období, které se opakuje každý rok. */
function VyberMesice({ hodnota, onZmena, konec, "aria-label": ariaLabel }) {
  const mm = /^\d{2}-\d{2}$/.test(String(hodnota || "")) ? String(hodnota).substring(0, 2) : "";
  return (
    <select className="field" value={mm} aria-label={ariaLabel}
      onChange={(e) => onZmena(e.target.value ? `${e.target.value}-${konec ? POSLEDNI[Number(e.target.value) - 1] : "01"}` : "")}>
      <option value="">celý rok</option>
      {MESICE.map((n, j) => <option key={n} value={String(j + 1).padStart(2, "0")}>{n}</option>)}
    </select>
  );
}

/**
 * Rozvrh: které dny, v kolik a v kterou část roku.
 * Do 16.9.2026 měl běh jeden čas na všechny dny, takže ranní termín se nedal
 * zadat vedle podvečerního. A protože se v zimě chodí do haly, může mít jeden
 * den v roce dva časy: Kbely mají úterý venku v 18:00 a v hale v 19:00.
 */
export function RozvrhEditor({ rozvrh = [], onZmena }) {
  const serad = (pole) => [...pole].sort((a, b) => (Number(a.den) - Number(b.den)) || String(a.cas_od).localeCompare(String(b.cas_od)));
  const dnyVRozvrhu = new Set(rozvrh.map((r) => Number(r.den)));
  const sezonni = rozvrh.some((r) => r.od || r.do);

  function prepni(den) {
    if (dnyVRozvrhu.has(den)) {
      onZmena(serad(rozvrh.filter((r) => Number(r.den) !== den)));
      return;
    }
    const vzor = rozvrh[0] || {};
    onZmena(serad([...rozvrh, { den, cas_od: vzor.cas_od || "18:00", cas_do: vzor.cas_do || "19:00", od: "", do: "" }]));
  }
  function uprav(i, klic, hodnota) {
    onZmena(serad(rozvrh.map((r, j) => (j === i ? { ...r, [klic]: hodnota } : r))));
  }
  function pridejCas(den) {
    const stejny = rozvrh.filter((r) => Number(r.den) === den);
    const vzor = stejny[stejny.length - 1] || {};
    onZmena(serad([...rozvrh, { den, cas_od: vzor.cas_od || "18:00", cas_do: vzor.cas_do || "19:00", od: "", do: "" }]));
  }

  const radky = serad(rozvrh);

  return (
    <div>
      <Nadpis ikona={Clock} popis="Klikni na dny, ve kterých se cvičí. Každý den může mít jiný čas, a když se čas v zimě mění, přidej si k tomu dni druhý s obdobím.">
        Rozvrh
      </Nadpis>
      <div className="flex flex-wrap gap-1.5">
        {DNY.map((d) => (
          <button key={d.id} type="button" onClick={() => prepni(d.id)}
            className={`px-3 py-1.5 rounded-xl text-sm font-medium border transition ${
              dnyVRozvrhu.has(d.id)
                ? "bg-brand-600 border-brand-600 text-white"
                : "bg-white border-slate-200 text-slate-600 hover:border-slate-300"}`}>
            {d.zkratka}
          </button>
        ))}
      </div>

      {radky.length > 0 && (
        <div className="mt-2.5 space-y-2">
          {radky.map((r, i) => {
            const den = DNY.find((d) => d.id === Number(r.den));
            const prvniVDni = i === 0 || Number(radky[i - 1].den) !== Number(r.den);
            const kolikVDni = radky.filter((x) => Number(x.den) === Number(r.den)).length;
            return (
              <div key={`${r.den}-${i}`} className="flex flex-wrap items-center gap-2">
                <span className="w-24 shrink-0 text-[15px] font-medium text-slate-700">
                  {prvniVDni ? (den ? den.nazev : r.den) : ""}
                </span>
                <input type="time" className="field w-auto" value={r.cas_od || ""} aria-label="Od"
                  onChange={(e) => uprav(i, "cas_od", e.target.value)} />
                <span className="text-slate-400">až</span>
                <input type="time" className="field w-auto" value={r.cas_do || ""} aria-label="Do"
                  onChange={(e) => uprav(i, "cas_do", e.target.value)} />
                {(sezonni || kolikVDni > 1) && (
                  <>
                    <span className="text-sm text-slate-500">od</span>
                    <VyberMesice hodnota={r.od} onZmena={(v) => uprav(i, "od", v)} aria-label="Období od" />
                    <span className="text-sm text-slate-500">do konce</span>
                    <VyberMesice hodnota={r.do} konec onZmena={(v) => uprav(i, "do", v)} aria-label="Období do" />
                  </>
                )}
                {kolikVDni > 1 && <Smazat onClick={() => onZmena(serad(rozvrh.filter((_, j) => j !== i)))} popis="Smazat čas" />}
              </div>
            );
          })}
          <div className="flex flex-wrap gap-3 pt-0.5">
            {[...dnyVRozvrhu].sort((a, b) => a - b).map((d) => (
              <PridatOdkaz key={d} onClick={() => pridejCas(d)}>
                Jiný čas v {(DNY.find((x) => x.id === d) || {}).nazev}
              </PridatOdkaz>
            ))}
          </div>
        </div>
      )}
      {radky.length === 0 && (
        <div className="mt-2 text-xs text-rose-600">Bez rozvrhu se lekce nevygenerují.</div>
      )}
    </div>
  );
}

/**
 * Sezónní místa setkání. Lokalita je celoroční, ale sraz se stěhuje:
 * v zimě do haly, zbytek roku ven. Období se zadává po měsících, protože
 * tak se to v praxi domlouvá, a opakuje se každý rok.
 */
export function MistaEditor({ mista = [], onZmena, misto }) {
  const uprav = (i, klic, hodnota) => onZmena(mista.map((m, j) => (j === i ? { ...m, [klic]: hodnota } : m)));

  return (
    <div>
      <Nadpis ikona={MapPin} popis="Kde se sraz koná v kterou část roku. Bez období platí místo celý rok. Nové lekce dostanou místo automaticky.">
        Místa setkání
      </Nadpis>
      <div className="space-y-2">
        {mista.map((m, i) => (
          <div key={i} className="rounded-xl border border-slate-200 p-2.5">
            <div className="flex items-start gap-2">
              <div className="grid flex-1 grid-cols-1 gap-2 sm:grid-cols-2">
                <input className="field" placeholder="Název (Sportovní hala Kbely)" value={m.nazev || ""}
                  onChange={(e) => uprav(i, "nazev", e.target.value)} />
                <input className="field" placeholder="Adresa (Toužimská 732, Praha 9)" value={m.adresa || ""}
                  onChange={(e) => uprav(i, "adresa", e.target.value)} />
                <input className="field sm:col-span-2" placeholder="Odkaz na mapu (nepovinné)" value={m.mapa || ""}
                  onChange={(e) => uprav(i, "mapa", e.target.value)} />
                <div className="flex items-center gap-2 sm:col-span-2">
                  <span className="shrink-0 text-sm text-slate-500">Od</span>
                  <VyberMesice hodnota={m.od} onZmena={(v) => uprav(i, "od", v)} aria-label="Místo od" />
                  <span className="shrink-0 text-sm text-slate-500">do konce</span>
                  <VyberMesice hodnota={m.do} konec onZmena={(v) => uprav(i, "do", v)} aria-label="Místo do" />
                </div>
              </div>
              <Smazat onClick={() => onZmena(mista.filter((_, j) => j !== i))} popis="Smazat místo setkání" />
            </div>
          </div>
        ))}
        {mista.length === 0 && (
          <div className="rounded-xl border border-dashed border-slate-200 px-3 py-4 text-sm text-slate-500">
            Zatím žádné. Lekce dostanou místo {misto ? <b>{misto}</b> : "podle názvu lokality"}.
          </div>
        )}
        <PridatOdkaz onClick={() => onZmena([...mista, { nazev: "", adresa: "", mapa: "", od: "", do: "" }])}>
          Přidat místo setkání
        </PridatOdkaz>
      </div>
    </div>
  );
}

/** Pauzy, typicky vánoční. V těchhle dnech se lekce negenerují. */
export function PauzyEditor({ pauzy = [], onZmena }) {
  const uprav = (i, klic, hodnota) => onZmena(pauzy.map((p, j) => (j === i ? { ...p, [klic]: hodnota } : p)));
  return (
    <div>
      <Nadpis ikona={CalendarOff} popis="Dny, kdy se necvičí. Lekce se v nich nezaloží a klientky pauzu uvidí v aplikaci.">
        Pauzy
      </Nadpis>
      <div className="space-y-2">
        {pauzy.map((p, i) => (
          <div key={i} className="flex items-center gap-2">
            <input type="date" className="field" value={p.od || ""} aria-label="Pauza od"
              onChange={(e) => uprav(i, "od", e.target.value)} />
            <span className="text-slate-400">až</span>
            <input type="date" className="field" value={p.do || ""} aria-label="Pauza do"
              onChange={(e) => uprav(i, "do", e.target.value)} />
            <input className="field" placeholder="Důvod (Vánoční pauza)" value={p.duvod || ""}
              onChange={(e) => uprav(i, "duvod", e.target.value)} />
            <Smazat onClick={() => onZmena(pauzy.filter((_, j) => j !== i))} popis="Smazat pauzu" />
          </div>
        ))}
        {pauzy.length === 0 && (
          <div className="rounded-xl border border-dashed border-slate-200 px-3 py-4 text-sm text-slate-500">
            Žádná pauza, cvičí se pořád.
          </div>
        )}
        <PridatOdkaz onClick={() => onZmena([...pauzy, { od: "", do: "", duvod: "" }])}>Přidat pauzu</PridatOdkaz>
      </div>
    </div>
  );
}
